import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as data from './mock-data';

const LIST_FALLBACK: Record<string, unknown> = {
  data: [],
  reports: [],
  threads: [],
  count: 0,
};

interface Route {
  method: string;
  pattern: RegExp;
  body: (match: RegExpMatchArray, req: HttpRequest<unknown>) => unknown;
}

const routes: Route[] = [
  { method: 'GET', pattern: /\/accountancy\/companies(\b|\?|$)/, body: () => data.mockAssignedCompanies },
  { method: 'GET', pattern: /\/accountancy\/company(\b|\?|$)/, body: () => data.mockCompanyTaxInfo },
  { method: 'POST', pattern: /\/accountancy\/ledger\/accounts(\b|\?|$)/, body: (_m, req) => data.mockChartAccountCreate(req) },
  { method: 'PATCH', pattern: /\/accountancy\/ledger\/accounts\/([^/]+)$/, body: (m, req) => data.mockChartAccountUpdate(m, req) },
  { method: 'GET', pattern: /\/accountancy\/ledger\/accounts(\b|\?|\/|$)/, body: () => data.mockChartAccounts },
  { method: 'POST', pattern: /\/accountancy\/ledger\/entries\/([^/]+)\/reverse$/, body: (m) => data.mockJournalEntryReverse(m) },
  { method: 'POST', pattern: /\/accountancy\/ledger\/entries(\b|\?|$)/, body: (_m, req) => data.mockJournalEntryCreate(req) },
  { method: 'GET', pattern: /\/accountancy\/ledger\/general-ledger(\b|\?|$)/, body: (_m, req) => data.mockGeneralLedger(req) },
  { method: 'GET', pattern: /\/accountancy\/ledger\/kpir(\b|\?|$)/, body: (_m, req) => data.mockKpir(req) },
  { method: 'GET', pattern: /\/accountancy\/ledger\/income-statement(\b|\?|$)/, body: (_m, req) => data.mockIncomeStatement(req) },
  { method: 'GET', pattern: /\/accountancy\/ledger\/balance-sheet(\b|\?|$)/, body: (_m, req) => data.mockBalanceSheet(req) },
  { method: 'PUT', pattern: /\/accountancy\/ledger\/opening-balances(\b|\?|$)/, body: (_m, req) => data.mockOpeningBalancesSave(req) },
  { method: 'GET', pattern: /\/accountancy\/ledger\/opening-balances(\b|\?|$)/, body: (_m, req) => data.mockOpeningBalances(req) },
  { method: 'POST', pattern: /\/accountancy\/ledger\/periods\/([^/]+)\/close$/, body: (m) => data.mockPeriodClose(m) },
  { method: 'POST', pattern: /\/accountancy\/ledger\/periods\/([^/]+)\/reopen$/, body: (m) => data.mockPeriodReopen(m) },
  { method: 'GET', pattern: /\/accountancy\/ledger\/periods(\b|\?|$)/, body: (_m, req) => data.mockLedgerPeriods(req) },
  { method: 'GET', pattern: /\/accountancy\/ledger\/entries(\b|\?|\/|$)/, body: (_m, req) => data.mockJournalEntriesFor(req) },
  { method: 'POST', pattern: /\/accountancy\/ledger\/post-depreciation(\b|\?|$)/, body: (_m, req) => data.mockPostDepreciation(req) },
  { method: 'POST', pattern: /\/accountancy\/ledger\/post-vat(\b|\?|$)/, body: (_m, req) => data.mockPostVat(req) },
  { method: 'POST', pattern: /\/accountancy\/ledger\/post-payroll(\b|\?|$)/, body: (_m, req) => data.mockPostPayroll(req) },
  { method: 'GET', pattern: /\/accountancy\/ledger\/payroll-aggregate(\b|\?|$)/, body: (_m, req) => data.mockPayrollAggregate(req) },
  { method: 'PUT', pattern: /\/accountancy\/accounting-settings(\b|\?|$)/, body: (_m, req) => data.mockAccountingSettingsUpdate(req) },
  { method: 'GET', pattern: /\/accountancy\/accounting-settings(\b|\?|$)/, body: () => data.mockAccountingSettings },
  { method: 'POST', pattern: /\/accountancy\/ai-entries(\b|\?|$)/, body: (_m, req) => data.mockManualEntryCreate(req) },
  { method: 'GET', pattern: /\/accountancy\/ai-entries(\b|\?|\/|$)/, body: () => data.mockAiEntries },
  { method: 'GET', pattern: /\/accountancy\/fiscal-reports\/export(\b|\?|$)/, body: () => new Blob(['﻿Numer;Typ;Urzadzenie;Okres od;Okres do;Data wystawienia;Netto;VAT;Brutto;Zaksiegowano\nRF/2025/05/M;monthly;PL0123456789;2025-05-01;2025-05-31;2025-05-31;78219.84;17990.56;96210.40;tak\nSUMA;;;;;;78219.84;17990.56;96210.40;'], { type: 'text/csv' }) },
  { method: 'POST', pattern: /\/accountancy\/fiscal-reports\/([^/]+)\/book$/, body: (m, req) => data.mockFiscalReportBook(m[1], req) },
  { method: 'GET', pattern: /\/accountancy\/fiscal-reports\/([^/]+)$/, body: (m) => data.mockFiscalReportById(m[1]) },
  { method: 'GET', pattern: /\/accountancy\/fiscal-reports(\b|\?|\/|$)/, body: () => data.mockFiscalReports },
  { method: 'GET', pattern: /\/accountancy\/tax-settlements\/suggest(\b|\?|$)/, body: (_m, req) => data.mockTaxSettlementSuggest(req) },
  { method: 'POST', pattern: /\/accountancy\/tax-settlements\/close$/, body: (_m, req) => data.mockTaxSettlementClose(req) },
  { method: 'POST', pattern: /\/accountancy\/tax-settlements\/reopen$/, body: (_m, req) => data.mockTaxSettlementReopen(req) },
  { method: 'POST', pattern: /\/accountancy\/tax-settlements\/mark-paid$/, body: (_m, req) => data.mockTaxSettlementMarkPaid(req) },
  { method: 'GET', pattern: /\/accountancy\/tax-settlements(\b|\?|\/|$)/, body: () => data.mockTaxSettlements },
  { method: 'GET', pattern: /\/accountancy\/vat-declarations(\b|\?|\/|$)/, body: () => data.mockDeclarations },
  { method: 'GET', pattern: /\/accountancy\/submissions\/([^/]+)\/file$/, body: () => new Blob(['<?xml version="1.0" encoding="UTF-8"?>\n<Declaration schema="JPK_V7M(2)" type="JPK_VAT" period="2025-05"><Payload>{}</Payload></Declaration>'], { type: 'application/xml' }) },
  { method: 'GET', pattern: /\/accountancy\/submissions\/([^/]+)\/upo$/, body: (m) => new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n<UPO>\n  <NumerReferencyjny>${m[1]}</NumerReferencyjny>\n  <Status>200</Status>\n  <Opis>Przetwarzanie dokumentu zakończone poprawnie, pobierz UPO</Opis>\n  <DataOdbioru>2025-06-25T09:14:02Z</DataOdbioru>\n</UPO>`], { type: 'application/xml' }) },
  { method: 'POST', pattern: /\/accountancy\/submissions\/([^/]+)\/refresh-status$/, body: (m) => data.mockSubmissionRefresh(m[1]) },
  { method: 'POST', pattern: /\/accountancy\/submissions\/([^/]+)\/send$/, body: (m) => data.mockSubmissionSend(m[1]) },
  { method: 'GET', pattern: /\/accountancy\/submissions(\b|\?|\/|$)/, body: () => data.mockSubmissions },
  { method: 'GET', pattern: /\/accountancy\/vat-registers\/([^/]+)\/records$/, body: (m) => data.mockVatRecordsFor(m[1]) },
  { method: 'GET', pattern: /\/accountancy\/vat-registers(\b|\?|\/|$)/, body: () => data.mockVatRegisters },
  { method: 'GET', pattern: /\/accountancy\/vat-records(\b|\?|\/|$)/, body: () => data.mockVatRecords },
  { method: 'GET', pattern: /\/accountancy\/comarch\/connectors(\b|\?|\/|$)/, body: () => data.mockComarchConnectors },
  { method: 'GET', pattern: /\/accountancy\/comarch\/exports(\b|\?|\/|$)/, body: () => data.mockComarchExports },
  { method: 'GET', pattern: /\/accountancy\/threads\/([^/]+)$/, body: (m) => data.mockThreadById(m[1]) },
  { method: 'GET', pattern: /\/accountancy\/threads(\b|\?|\/|$)/, body: () => data.mockThreads },
  { method: 'GET', pattern: /\/accountancy\/timetracking\/timers\/current$/, body: () => data.mockTimerCurrent() },
  { method: 'POST', pattern: /\/accountancy\/timetracking\/timers\/start$/, body: (_m, req) => data.mockTimerStart(req) },
  { method: 'POST', pattern: /\/accountancy\/timetracking\/timers\/stop$/, body: (_m, req) => data.mockTimerStop(req) },
  { method: 'GET', pattern: /\/accountancy\/timetracking\/reports\/summary$/, body: (_m, req) => data.mockTimeReportSummary(req) },
  { method: 'GET', pattern: /\/accountancy\/timetracking\/entries$/, body: (_m, req) => data.mockTimeEntriesList(req) },
  { method: 'POST', pattern: /\/accountancy\/timetracking\/entries$/, body: (_m, req) => data.mockTimeEntryCreate(req) },
  { method: 'PATCH', pattern: /\/accountancy\/timetracking\/entries\/([^/]+)$/, body: (m, req) => data.mockTimeEntryUpdate(m, req) },
  { method: 'DELETE', pattern: /\/accountancy\/timetracking\/entries\/([^/]+)$/, body: (m) => data.mockTimeEntryDelete(m) },
  { method: 'POST', pattern: /\/accountancy\/timetracking\/invoice-draft$/, body: (_m, req) => data.mockInvoiceDraft(req) },
  { method: 'GET', pattern: /\/accountancy\/vehicles\/[^/]+\/settlements\/[^/]+\/optima\.csv$/, body: () => new Blob(['okres;pojazd\n2026-06;Skoda Octavia'], { type: 'text/csv' }) },
  { method: 'GET', pattern: /\/accountancy\/vehicles\/([^/]+)\/settlements(\b|\?|\/|$)/, body: (m) => data.mockVehicleSettlements(m[1]) },
  { method: 'GET', pattern: /\/accountancy\/vehicles(\b|\?|\/|$)/, body: () => data.mockClientVehicles },
  { method: 'POST', pattern: /\/accountancy\/fixed-assets\/depreciations\/run$/, body: () => ({ booked: 2, posted: 2, skipped: 0, blocked: 0 }) },
  { method: 'GET', pattern: /\/accountancy\/fixed-assets\/depreciations\/optima\.csv/, body: () => new Blob(['﻿Okres;Nr inwentarzowy;Nazwa\n2025-02;ST/2025/001;Laptop Dell XPS 15'], { type: 'text/csv' }) },
  { method: 'GET', pattern: /\/accountancy\/fixed-assets\/[^/]+\/plan(\b|\?|$)/, body: () => data.mockFixedAssetPlan },
  { method: 'GET', pattern: /\/accountancy\/fixed-assets\/[^/]+\/improvements(\b|\?|$)/, body: () => data.mockFixedAssetImprovements },
  { method: 'GET', pattern: /\/accountancy\/fixed-assets\/([^/]+)\/disposal(\b|\?|$)/, body: (m) => data.mockFixedAssetDisposalFor(m[1]) },
  { method: 'GET', pattern: /\/accountancy\/fixed-assets\/[^/]+\/depreciations\/[^/]+\/optima\.csv$/, body: () => new Blob(['﻿Okres;Nr inwentarzowy;Nazwa\n2025-02;ST/2025/001;Laptop Dell XPS 15'], { type: 'text/csv' }) },
  { method: 'GET', pattern: /\/accountancy\/fixed-assets\/[^/]+\/depreciations(\b|\?|$)/, body: () => data.mockFixedAssetDepreciations },
  { method: 'GET', pattern: /\/accountancy\/fixed-assets\/([^/]+)$/, body: (m) => data.mockFixedAssetById(m[1]) },
  { method: 'GET', pattern: /\/accountancy\/fixed-assets(\b|\?|$)/, body: () => data.mockFixedAssets },
  { method: 'GET', pattern: /\/support\/tickets\/([^/]+)$/, body: (m) => data.mockSupportTicketById(m[1]) },
  { method: 'GET', pattern: /\/support\/tickets(\b|\?|$)/, body: () => data.mockSupportTickets },
];

@Injectable()
export class MockBackendInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isMockTarget(req.url)) {
      return next.handle(req);
    }

    if (this.isKeycloak(req.url)) {
      return this.respond(req, this.keycloakBody(req.url));
    }

    const path = this.pathOf(req.url);
    for (const route of routes) {
      if (route.method !== req.method) continue;
      const match = path.match(route.pattern);
      if (match) {
        try {
          return this.respond(req, route.body(match, req));
        } catch (err) {
          if (err instanceof data.MockHttpError) {
            return throwError(
              () => new HttpErrorResponse({ status: err.status, error: err.body, url: req.url }),
            );
          }
          throw err;
        }
      }
    }

    return this.respond(req, this.fallbackBody(req));
  }

  private isMockTarget(url: string): boolean {
    return url.startsWith(environment.apiUrl) || this.isKeycloak(url);
  }

  private isKeycloak(url: string): boolean {
    return url.startsWith(environment.keycloak.url) || url.includes('/realms/');
  }

  private keycloakBody(url: string): unknown {
    if (url.includes('/logout')) return {};
    return data.mockKeycloakToken();
  }

  private pathOf(url: string): string {
    return url.startsWith(environment.apiUrl) ? url.slice(environment.apiUrl.length) : url;
  }

  private fallbackBody(req: HttpRequest<unknown>): unknown {
    if (req.responseType === 'blob') return new Blob([], { type: 'application/octet-stream' });
    if (req.method === 'DELETE' || req.method === 'PATCH' || req.method === 'PUT') return {};
    if (req.method === 'POST' && !/\/list$|\/search$/.test(this.pathOf(req.url))) {
      return { id: 'mock-' + Math.random().toString(36).slice(2, 10), ok: true };
    }
    return LIST_FALLBACK;
  }

  private respond(req: HttpRequest<unknown>, body: unknown): Observable<HttpEvent<unknown>> {
    const responseBody = req.responseType === 'blob' && !(body instanceof Blob)
      ? new Blob([JSON.stringify(body)], { type: 'application/json' })
      : body;
    return of(new HttpResponse({ status: 200, body: responseBody, url: req.url })).pipe(delay(80));
  }
}
